import { useContext, useCallback, useEffect, useState, useMemo } from 'react';

import classNames from 'classnames';
import { differenceInDays } from 'date-fns';
import { useInView } from 'react-intersection-observer';
import { useHistory } from 'react-router';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { RefillSources, ANALYTICS_EVENTS, UNIFIED } from 'src/app/analytics/analyticsEvents';
import { AnimationWrapper } from 'src/app/components/Elements/Animation/AnimationWrapper';
import LinkButton from 'src/app/components/Elements/Button/LinkButton';
import { EmptyStateCard } from 'src/app/components/Elements/Card/EmptyStateCard';
import { Divider } from 'src/app/components/Elements/Divider';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import Spinner from 'src/app/components/Elements/Spinner';
import { StyledLink } from 'src/app/components/Elements/StyledLink';
import { ViewOrderToast } from 'src/app/components/Elements/ToastBanner/presets/ViewOrder';
import { Typography } from 'src/app/components/Elements/Typography';
import { RequestRefillModal } from 'src/app/components/Modal/RequestRefill/container';
import { getCheckoutPath } from 'src/app/components/Pages/Essentials/CheckoutAddons/utils';
import { DoctorCard } from 'src/app/components/Pages/Medications/DoctorCard';
import NextBestActions from 'src/app/components/Pages/Medications/NextBestActions';
import { useAccount } from 'src/app/hooks/data/account';
import { useIsHealthProfileUpdateDue } from 'src/app/hooks/data/healthProfile';
import { usePendingPrescriptionTransfers } from 'src/app/hooks/data/prescriptionTransfers';
import { useUser } from 'src/app/hooks/data/user';
import { useHealthProfileSessionData } from 'src/app/hooks/feature_data/useHealthProfileSessionData';
import {
  useLoggedInTransferFormData,
  useLoggedInTransferFormProgress,
} from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import type { MedicationCardContextType } from 'src/app/providers/MedicationCardStateProvider';
import { MedicationCardStateContext } from 'src/app/providers/MedicationCardStateProvider';
import { loggedInHealthProfile, loggedInTransferRx, showYourDoctor } from 'src/app/router/paths';
import {
  segmentAnalyticsEvent,
  segmentTrack,
  sessionSetCurrentUserFromV3User,
  toggleHideHeader,
} from 'src/app/state/actions';
import { fetchHomescreen, loadHomescreenMedications } from 'src/app/state/actions/homescreen';
import { getCurrentPathname, getFacility, getIsFetchingMedications } from 'src/app/state/selectors';
import { getIsFetchingArchivedMedications, loadArchivedMedications } from 'src/app/state/slices/archivedMedications';
import { openChat } from 'src/app/state/slices/chat';
import { useDispatch, useSelector } from 'src/app/state/store';
import type { HomePrescriptionItem, OptimisticPendingTransfer } from 'src/app/types';
import { flattenPrescriptionItems } from 'src/app/utils/medicationUtils';
import { useLogRocketInit } from 'src/hooks/useLogRocketInit';

import { TransparentButton } from '../../Elements/Button/TransparentButton';
import { LegalLinks } from '../../Elements/LegalLinks';
import { ToastBanner } from '../../Elements/ToastBanner';
import { HealthProfileUpdateStepNames } from '../HealthProfileUpdate/constants';

import type { PropsFromRedux } from './container';
import { PendingMedicationSVG } from './CustomComponents';
import { GroupedMedications } from './GroupedMedications';
import { ProcessingEscripts } from './ProcessingEscripts';
import { DoctorCardSection, DoctorText, DoctorVisitsText, HeaderText, Image, NoMedicationsInfo } from './styles';
import styles from './styles.module.scss';

interface MedicationsProps extends PropsFromRedux {}

const Medications = ({
  activeHomescreenMedicationsCount,
  filteredHomescreenMedications,
  hasCart,
  allPatients,
  hasMultiplePatients,
  numPendingMeds,
  optimisticPendingTransfers,
  showSecondaryStyle,
  totalQuantity,
}: MedicationsProps) => {
  useSegmentTrack(UNIFIED.viewMedications);
  const trackBeginCheckoutEvent = useSegmentEvent('beginCheckout', { shouldTrackOnMount: false });
  const { push } = useHistory();
  const { medicationCardState, saveMedicationCardState } = useContext(
    MedicationCardStateContext
  ) as MedicationCardContextType;

  /*
   Keeping this code here instead of ripping everything out because we will keep the layout elements
   for future use. The original feature powered by this, however, is deprecated and we will build new
   feature flags or experiments when the time comes.
  */
  const visibleNextBestActionCard = false;
  const isFetchingMedications = useSelector(getIsFetchingMedications);
  const isFetchingArchiveMedications = useSelector(getIsFetchingArchivedMedications);
  const facility = useSelector(getFacility);
  const pagePath = useSelector(getCurrentPathname);

  const hasActiveMeds = activeHomescreenMedicationsCount > 0;

  const [isInStreamlineCTAFeature, setIsInStreamlineCTAFeature] = useState<boolean>(false);
  const [isUpsellEssentialsEnabled, setUpsellEssentialsEnabled] = useState<boolean>(false);
  const [hasExperimentsLoading, setHasExperimentsLoading] = useState<boolean>(true);

  // Health Profile form redirect logic
  const primaryPatientId = useMemo(() => allPatients?.find(patient => patient.isPrimary)?.id, [allPatients]);
  const {
    data: isHealthProfileUpdateDue,
    isLoading: isLoadingHealthProfileUpdateDue,
    isError: isErrorLoadingHealthProfileUpdateDue,
  } = useIsHealthProfileUpdateDue(primaryPatientId);
  const { healthProfileSessionData, isLoadingSessionData } = useHealthProfileSessionData();

  useEffect(() => {
    if (!isLoadingSessionData) {
      const hasSeenFormThisSession = healthProfileSessionData?.hasSeenForm;
      if (!hasSeenFormThisSession && !!!isErrorLoadingHealthProfileUpdateDue && !!!isLoadingHealthProfileUpdateDue) {
        const checkHealthProfileUpdateDue = async () => {
          if (!!isHealthProfileUpdateDue) {
            // Redirect to health profile form if the user is eligible and hasn't seen it yet this session
            push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.SelectTakingMedications }));
          }
        };
        checkHealthProfileUpdateDue();
      }
    }
  }, [
    isHealthProfileUpdateDue,
    healthProfileSessionData,
    isLoadingSessionData,
    primaryPatientId,
    push,
    isLoadingHealthProfileUpdateDue,
    isErrorLoadingHealthProfileUpdateDue,
  ]);

  useLogRocketInit({ condition: !hasExperimentsLoading && isUpsellEssentialsEnabled });

  const dispatch = useDispatch();
  /*
   * RX Live form may leave the header hidden in some situations when the user leaves. Since
   * the expected state is that it's always on, we'll force it on here when loading Home.
   */
  useEffect(() => {
    dispatch(toggleHideHeader(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loggedInUser, isLoggedIn } = useAuth();
  const { data: user, isSuccess: isLoadUserSuccess } = useUser(
    loggedInUser ? loggedInUser.uuid : '',
    Boolean(loggedInUser)
  );

  const { data: account, isSuccess: hasAccountLoaded } = useAccount(user ? parseInt(user.accountId) : -1, {
    enabled: Boolean(isLoggedIn),
    refetchOnWindowFocus: false,
  });

  const accountAgeInDays = useMemo(() => {
    if (!account) {
      return null;
    }
    const accountCreatedAtDate = new Date(account.user.createdAt);
    const now = new Date();
    return differenceInDays(now, accountCreatedAtDate);
  }, [account]);

  const { data: pendingTransfers } = usePendingPrescriptionTransfers(
    user ? parseInt(user.accountId) : -1,
    Boolean(user)
  );
  const flatPredingTransfers = pendingTransfers ? flattenPrescriptionItems(pendingTransfers) : [];
  const hasNoMeds = activeHomescreenMedicationsCount === 0 && (flatPredingTransfers?.length ?? 0) === 0;

  const dispatchTrackClickTransfer = () => dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.medicationsTransferRx));
  const dispatchOpenChat = () => dispatch(openChat());
  const dispatchSegmentTrack = useCallback(
    (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
    [dispatch]
  );

  const { setLoggedInTransferFormData } = useLoggedInTransferFormData();
  const { setLoggedInTransferFormProgress } = useLoggedInTransferFormProgress();

  useEffect(() => {
    const loadData = async () => {
      if (isLoadUserSuccess && user) {
        dispatch(sessionSetCurrentUserFromV3User(user));
        if (user.accountId) {
          dispatch(loadHomescreenMedications(user.accountId));
          dispatch(loadArchivedMedications(user.accountId));
          dispatch(fetchHomescreen());
          //await dispatch(fetchPendingTransfers(user.accountId));
        }
      }
    };

    loadData();
    // Reset transfer form data
    setLoggedInTransferFormProgress({});
    setLoggedInTransferFormData({});
  }, [dispatch, isLoadUserSuccess, user]);

  const { inView, ref } = useInView({ threshold: 0.5 });
  const { fetchVariant } = useExperiments();

  useEffect(() => {
    if (!!user && !!hasAccountLoaded && facility) {
      setHasExperimentsLoading(true);
      const fetchStreamlineCTAFeature = async () => {
        const variant = await fetchVariant({
          experimentKey: 'streamline_cta',
          userId: user.accountId,
          audienceAttributes: {
            created_days_ago: accountAgeInDays,
          },
        });
        setIsInStreamlineCTAFeature(variant === 'on');
      };

      const fetchUpsellEssentialsExperiment = async () => {
        const variant = await fetchVariant({
          experimentKey: 'upsell_essentials_12_21_23',
          userId: user.accountId,
          audienceAttributes: {
            facility_code: facility?.code,
          },
        });
        setUpsellEssentialsEnabled(variant === 'on');
      };

      fetchStreamlineCTAFeature();
      fetchUpsellEssentialsExperiment();
    }
    setHasExperimentsLoading(false);
  }, [setIsInStreamlineCTAFeature, fetchVariant, user, accountAgeInDays, hasAccountLoaded, facility]);

  useEffect(() => {
    if (inView) {
      dispatchSegmentTrack(UNIFIED.viewShowYourDoctorCard, { source: 'Medication Page' });
    }
  }, [inView, isInStreamlineCTAFeature, facility, dispatchSegmentTrack]);

  const handleOpenLiveChat = e => {
    e.preventDefault();
    dispatchOpenChat();
  };

  const handleOnCancelForRequestingRefillClicked = () => {
    if (medicationCardState?.source === RefillSources.requestRefillRxTab) {
      dispatch(
        segmentTrack(UNIFIED.cancelRequestRefill, {
          patientmedication_ids: medicationCardState?.patientMedicationIdInRequestRefillState,
          source: medicationCardState?.source,
          cta_variation: 'button',
        })
      );
    } else {
      dispatch(
        segmentTrack(UNIFIED.cancelProcessRefill, {
          patientmedication_ids: medicationCardState?.patientMedicationIdInProcessRefillState,
          source: medicationCardState?.source,
          cta_variation: 'button',
        })
      );
    }
    clearTimeout(medicationCardState?.refillRequestTimeoutId);
    saveMedicationCardState({ showRefillRequestedToastBanner: false });
    dispatch(loadHomescreenMedications(user?.accountId));
    dispatch(fetchHomescreen());
  };

  if (hasExperimentsLoading)
    return (
      <StandardPage>
        <Spinner centered />
      </StandardPage>
    );
  if (isFetchingMedications || isFetchingArchiveMedications) {
    return (
      <StandardPage>
        <div>
          <Typography variant={'h1'} marginBottom={'lg'}>
            Prescriptions
          </Typography>
          <Spinner centered />
        </div>
      </StandardPage>
    );
  }

  /**
   * Render home pending items if they exist, other wise render optimistic
   * pending transfers
   */
  const pendingItems: HomePrescriptionItem[] | OptimisticPendingTransfer[] =
    flatPredingTransfers ?? optimisticPendingTransfers;

  return (
    <StandardPage>
      {visibleNextBestActionCard && <NextBestActions visibleNextBestActionCard={visibleNextBestActionCard} />}
      {pendingItems?.length > 0 && (
        <>
          <Typography variant={'h2'} marginBottom={'s3'}>
            Pending requests
          </Typography>
          <div className={styles.pendingComponent}>
            <GroupedMedications
              pendingItems={pendingItems}
              showPendingText={true}
              handleOpenLiveChat={handleOpenLiveChat}
            />
          </div>
        </>
      )}
      <div className={classNames(styles.medicationHeaderWrapper)}>
        <Typography variant={'h1'} marginBottom={'s3'}>
          Prescriptions
        </Typography>
      </div>

      <div className={styles.component}>
        <RequestRefillModal />

        {!hasActiveMeds && !(pendingItems?.length > 0) ? (
          <ProcessingEscripts handleOpenLiveChat={handleOpenLiveChat} />
        ) : null}

        {activeHomescreenMedicationsCount === 0 && numPendingMeds > 0 && (
          <AnimationWrapper isCollapsed={false} isExpanded={true}>
            <EmptyStateCard
              height={32}
              image={<PendingMedicationSVG numItems={numPendingMeds} />}
              text={`You have ${numPendingMeds} pending medication${numPendingMeds > 1 ? 's' : ''}`}
              textStyle={'blueBold'}
            />
          </AnimationWrapper>
        )}

        {hasNoMeds ? (
          <>
            <AnimationWrapper isCollapsed={false} isExpanded={true}>
              <EmptyStateCard
                height={32}
                image={<Image src={'/public/home-rx.svg'} alt="Medication Icon" />}
                text={
                  hasMultiplePatients
                    ? 'Looks like you and your family don’t have any medications.'
                    : 'Looks like you don’t have any medications.'
                }
                textStyle="blueBold"
              />
            </AnimationWrapper>
          </>
        ) : (
          <GroupedMedications
            homescreenItemsByPatient={filteredHomescreenMedications}
            hasMultiplePatients={hasMultiplePatients}
            showNoActiveMedications={activeHomescreenMedicationsCount > 0}
          />
        )}

        <NoMedicationsInfo
          anchorText="Why don’t I see my medications?"
          header="Why don’t I see my medications?"
          body="If your doctor sent us a prescription, we’ll text you within an hour of receiving it to schedule delivery."
          analyticTip="Why No Rx?"
          placement="top-start"
        />

        {isInStreamlineCTAFeature ? (
          <>
            <LinkButton
              to={loggedInTransferRx.toPath()}
              style={'secondary'}
              size="medium"
              block
              onClick={() => {
                dispatchTrackClickTransfer();
                dispatchSegmentTrack(UNIFIED.viewTransferForm, { location: 'Your Prescriptions' });
              }}
            >
              Transfer Medications
            </LinkButton>
            <Divider margin="3.2rem 0 0" />
            {facility && (
              <DoctorCardSection ref={ref}>
                <DoctorText>
                  <div>
                    <DoctorVisitsText variant={'h2'}>For doctor visits</DoctorVisitsText>
                    <Typography variant={'body'}>Share this card or just say "Capsule"</Typography>
                  </div>
                </DoctorText>

                <DoctorCard facility={facility} pagePath={pagePath} />
              </DoctorCardSection>
            )}
          </>
        ) : (
          <>
            <Divider margin="0" />

            <HeaderText>Manage all your meds with Capsule</HeaderText>
            <LinkButton
              to={loggedInTransferRx.toPath()}
              style={showSecondaryStyle ? 'secondary' : 'primary'}
              size="medium"
              block
              onClick={() => {
                dispatchTrackClickTransfer();
                dispatchSegmentTrack(UNIFIED.viewTransferForm, { location: 'Your Prescriptions' });
              }}
            >
              Transfer Medications
            </LinkButton>
            <LinkButton
              to={showYourDoctor.toPath()}
              style="secondary"
              size="medium"
              block
              onClick={dispatchTrackClickTransfer}
            >
              Show Your Doctor
            </LinkButton>
          </>
        )}
        <LegalLinks />

        {medicationCardState?.showRefillRequestedToastBanner && (
          <FixedFloat>
            <ToastBanner
              text={'Refill requested'}
              textVariant={'body'}
              variant={'refillRequested'}
              medicationsHomepageHack={true}
            >
              <TransparentButton onClick={handleOnCancelForRequestingRefillClicked}>
                <Typography variant="body" color="capsuleGreen50">
                  Cancel
                </Typography>
              </TransparentButton>
            </ToastBanner>
          </FixedFloat>
        )}

        {hasCart && (
          <FixedFloat>
            <StyledLink
              variant={'noTextDecoration'}
              aria-label="Begin Checkout"
              onClick={() => trackBeginCheckoutEvent()}
              to={getCheckoutPath({ isUpsellEssentialsEnabled })}
            >
              <div data-cy="checkout-button">
                <ViewOrderToast cartItemCount={totalQuantity} />
              </div>
            </StyledLink>
          </FixedFloat>
        )}
      </div>
    </StandardPage>
  );
};

export default Medications;
